import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import { Close } from "@mui/icons-material";
import { compareAsc, compareDesc } from "date-fns";
import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "./AuthProvider";
import ConfirmationDialog from "./ConfirmationDialog";
import Loader from "./loader/Loader";
import NotesService from "./services/notesService";
import Note from "./ShortNote";
import { SnackbarContext } from "./SnackbarProvider";

const useStyles = makeStyles((theme) => ({
  cardGrid: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(8),
  },
  search: {
    maxWidth: "180px",
  },
  sort: {
    width: "180px",
  },
}));

const NotesListShort = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [dialogData, setDialogData] = useState({ open: false });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("modified-desc");

  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const { user } = useContext(AuthContext);
  const { openErrorSnack } = useContext(SnackbarContext);

  const catchError = useCallback(
    (error) => {
      console.error(error);
      openErrorSnack("An error occured during getting notes");
    },
    [openErrorSnack]
  );

  useEffect(() => {
    if (user && user.uid) {
      setIsLoading(true);
      NotesService.getAllShorts()
        .then((doc) => {
          const newNotes = doc.data() ?? {};
          setNotes(newNotes.shorts ?? []);
          setIsLoading(false);
        })
        .catch(catchError);
    }
  }, [user, catchError]);

  useEffect(() => {
    if (notes) {
      setFilteredNotes(
        notes
          .filter((note) =>
            note.name.toLowerCase().includes(search.toLowerCase())
          )
          .sort((a, b) => {
            const sortDate = sort.split("-")[0];
            const sortDirection = sort.split("-")[1];
            const sortFn = sortDirection === "asc" ? compareAsc : compareDesc;
            switch (sortDate) {
              case "created":
                return sortFn(new Date(a.created), new Date(b.created));
              case "modified":
                return sortFn(new Date(a.modified), new Date(b.modified));
              default:
                return sortFn(new Date(a.modified), new Date(b.modified));
            }
          })
      );
    }
  }, [search, sort, notes]);

  const onRemoveNote = (id) => {
    setDialogData({ open: true, noteId: id });
  };

  const removeNote = async () => {
    try {
      setIsLoading(true);
      // TODO promise.all or transactions
      await NotesService.remove(dialogData.noteId);
      await NotesService.removeShortById(dialogData.noteId);

      setIsLoading(true);
      NotesService.getAllShorts()
        .then((doc) => {
          setNotes(doc.data().shorts);
          setIsLoading(false);
        })
        .catch(catchError);
    } catch (e) {
      console.error("ERROR ", e);
      openErrorSnack("An error occured during removing note");
    }
  };

  const getHelperText = (count, max) => {
    if (count === 0) return "no results";
    if (count === 1) return "1 result";
    if (count < max) return `${count} results`;
    return "";
  };

  return (
    <Container className={classes.cardGrid} maxWidth="md">
      {isLoading ? (
        <Grid container justifyContent="center" style={{ marginTop: "20px" }}>
          <Grid item>
            <Loader />
          </Grid>
        </Grid>
      ) : (
        <>
          <Container
            maxWidth="xs"
            style={{
              marginBottom: "25px",
              display: "flex",
              justifyContent: "space-between",
              padding: "0",
            }}
          >
            <FormControl className={classes.search} size="small">
              <TextField
                id="search-input"
                label="Search"
                aria-label="Search"
                size="small"
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                error={filteredNotes.length === 0}
                helperText={getHelperText(filteredNotes.length, notes.length)}
                InputProps={{
                  autoComplete: "off",
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Clear search"
                        aria-controls="search-input"
                        onClick={() => setSearch("")}
                        edge="end"
                      >
                        <Close />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
            <FormControl className={classes.sort} size="small">
              <InputLabel id="sort-date-select">Sort by</InputLabel>
              <Select
                label="Sort by"
                name="sort-date"
                id="sort-date-select"
                onChange={(e) => setSort(e.target.value)}
                value={sort}
              >
                <MenuItem value="created-asc">creation asc</MenuItem>
                <MenuItem value="created-desc">creation desc</MenuItem>
                <MenuItem value="modified-asc">modification asc</MenuItem>
                <MenuItem value="modified-desc">modification desc</MenuItem>
              </Select>
            </FormControl>
          </Container>
          <Grid container spacing={4}>
            {notes.length === 0 && (
              <>
                <span>There are no notes yet. Add your first note</span>&nbsp;
                <Link to="/note">here</Link>
              </>
            )}
            {filteredNotes.length > 0 &&
              filteredNotes.map((note) => (
                <Grid item key={note.name} xs={12} sm={6} md={4}>
                  <Note note={note} onRemove={onRemoveNote} />
                </Grid>
              ))}
          </Grid>
          <p>You have {notes.length} notes in total</p>
        </>
      )}
      <ConfirmationDialog
        isOpen={dialogData.open}
        onClose={() => setDialogData({ open: false, noteId: undefined })}
        onConfirm={removeNote}
      />
    </Container>
  );
};

export default NotesListShort;
